import { useMemo, useState } from 'react';
import { Check, Circle, Star } from 'lucide-react';

import {
  Alert,
  Badge,
  Button,
  Card,
  HelpTip,
  Input,
  Label,
  PageHeader,
  Select,
  Switch,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
  EmptyState,
  IconPicker,
  useConfirmDialog,
} from '@/components/ui';
import { useDepartments, useUsers } from '@/lib/admin-store';
import {
  useWorkflows,
  useWorkflowActions,
  type Workflow,
  type CreateWorkflowInput,
  type UpdateWorkflowInput,
} from '@/lib/workflows-store';
// BUG-V2-020 / BF.1: seção de colunas no modal de edição.
import { WorkflowColumnsSection } from './components/WorkflowColumnsSection';

/**
 * /admin/workflows — fluxos de trabalho por departamento (R4.5).
 *
 * Layout: lista agrupada por dept ativo. Cada fluxo é uma linha com:
 * - estrela se padrão, check se ativo, círculo se inativo (ícones Lucide —
 *   FASE-C baniu emoji como ícone de status)
 * - Nome + identificador + descrição
 * - Ações: Editar | Excluir (soft)
 *
 * "Novo fluxo" no header de cada dept abre modal de criação. Modal
 * compartilhado para create+edit.
 *
 * Backend valida: identificador regex, UNIQUE (dept,slug), bloqueia delete do
 * padrão + delete do único ativo do dept.
 *
 * FASE-C (copy): "workflow" some da UI — vira "fluxo de trabalho"/"fluxo".
 * "slug" vira "Identificador" com <HelpTip> explicando onde ele é usado.
 */
export function WorkflowsPage() {
  // FASE 2 / M1: ConfirmDialog
  const confirm = useConfirmDialog();
  const departments = useDepartments();
  const { data: workflows = [], isLoading } = useWorkflows({ ativo: null });
  const { create, update, remove, pending } = useWorkflowActions();
  const [editing, setEditing] = useState<Workflow | null>(null);
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Agrupa workflows por dept
  const byDept = useMemo(() => {
    const m = new Map<string, Workflow[]>();
    for (const w of workflows) {
      const arr = m.get(w.deptSlug) ?? [];
      arr.push(w);
      m.set(w.deptSlug, arr);
    }
    return m;
  }, [workflows]);

  const activeDepts = useMemo(
    () => departments.filter((d) => d.ativo).sort((a, b) => a.ordem - b.ordem),
    [departments]
  );

  async function handleSave(input: CreateWorkflowInput | UpdateWorkflowInput) {
    setError(null);
    try {
      if (editing) {
        await update(editing.id, input as UpdateWorkflowInput);
      } else {
        await create(input as CreateWorkflowInput);
      }
      setEditing(null);
      setCreatingFor(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    }
  }

  async function handleDelete(w: Workflow) {
    const ok = await confirm({
      title: 'Excluir fluxo de trabalho',
      message: `Excluir o fluxo "${w.nome}"? Pode ser desfeito ativando de novo.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    setError(null);
    try {
      await remove(w.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  }

  return (
    <section>
      <PageHeader
        crumb="Configurações · Operação"
        title="Fluxos de trabalho"
        subtitle="Cada departamento pode ter vários fluxos (ex.: Comercial → Vendas, Renovações, Upgrades). Um deles é o padrão — é o que abre quando alguém clica no departamento pelo menu."
      />

      <Alert variant="info" rail className="mb-4">
        Você pode criar, editar e desativar fluxos aqui. Não dá pra excluir o último fluxo
        ativo de um departamento nem o fluxo padrão — marque outro como padrão antes.
      </Alert>

      {error && (
        <Alert variant="danger" rail className="mb-4">
          {error}
        </Alert>
      )}

      {isLoading ? (
        <div className="py-8 text-center text-sm text-ink-3 dark:text-ink-dark-3 italic">
          Carregando fluxos…
        </div>
      ) : activeDepts.length === 0 ? (
        <EmptyState
          title="Nenhum departamento ativo"
          description="Crie os departamentos em Configurações › Pessoas e acesso › Departamentos antes de montar os fluxos de trabalho."
        />
      ) : (
        <div className="space-y-4">
          {activeDepts.map((dept) => {
            const list = byDept.get(dept.slug) ?? [];
            return (
              <Card key={dept.slug}>
                <div className="p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <h3 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">
                        {dept.nome}
                      </h3>
                      {dept.descricao && (
                        <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
                          {dept.descricao}
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => setCreatingFor(dept.slug)}
                      data-testid={`workflow-new-${dept.slug}`}
                    >
                      + Novo fluxo
                    </Button>
                  </div>
                  {list.length === 0 ? (
                    <div className="text-xs text-ink-3 dark:text-ink-dark-3 italic py-2">
                      Sem fluxos por enquanto. Crie o primeiro pra começar.
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {list.map((w) => (
                        // BUG-V2-005 fix: row inteira é clicável (abre modal de
                        // edição). Antes só os botões "Editar"/"Excluir" tinham
                        // onClick — clique numa área "neutra" da row dava UX
                        // confusa de "404 ao clicar workflow". Sem rota nova.
                        <li
                          key={w.id}
                          onClick={() => setEditing(w)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setEditing(w);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={`Editar o fluxo ${w.nome}`}
                          className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-red"
                          data-testid={`workflow-row-${w.id}`}
                        >
                          <div className="flex min-w-0 flex-1 items-center gap-2">
                            <StatusFluxoIcone
                              padrao={w.defaultFlag}
                              ativo={w.ativo}
                            />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="min-w-0 break-words">{w.nome}</span>
                                {w.defaultFlag && <Badge variant="danger">Padrão</Badge>}
                                {!w.ativo && <Badge variant="neutral">Inativo</Badge>}
                              </div>
                              <div className="text-xs text-ink-3 dark:text-ink-dark-3 font-mono break-all">
                                {w.slug}
                              </div>
                              {w.descricao && (
                                <div className="text-xs text-ink-3 dark:text-ink-dark-3 truncate">
                                  {w.descricao}
                                </div>
                              )}
                            </div>
                          </div>
                          <div
                            className="flex shrink-0 flex-wrap items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditing(w)}
                              data-testid={`workflow-edit-${w.id}`}
                            >
                              Editar
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(w)}
                              disabled={!w.ativo || pending === w.id}
                              data-testid={`workflow-delete-${w.id}`}
                            >
                              Excluir
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {(editing || creatingFor) && (
        <WorkflowFormDialog
          workflow={editing}
          defaultDeptSlug={creatingFor}
          onClose={() => {
            setEditing(null);
            setCreatingFor(null);
            setError(null);
          }}
          onSave={handleSave}
          pending={pending}
        />
      )}
    </section>
  );
}

// ─── Ícone de status ────────────────────────────────────────────────────

/**
 * FASE-C: substitui os emoji ⭐ / ✓ / ◯ por ícones Lucide (a regra proíbe
 * emoji como ícone de status). O texto acessível continua vindo do title.
 */
function StatusFluxoIcone({ padrao, ativo }: { padrao: boolean; ativo: boolean }) {
  const rotulo = padrao ? 'Fluxo padrão do departamento' : ativo ? 'Ativo' : 'Inativo';
  const Icone = padrao ? Star : ativo ? Check : Circle;
  return (
    <span
      title={rotulo}
      aria-label={rotulo}
      role="img"
      className={
        padrao
          ? 'shrink-0 text-red dark:text-red-glow'
          : ativo
            ? 'shrink-0 text-success'
            : 'shrink-0 text-ink-3 dark:text-ink-dark-3'
      }
    >
      <Icone className="w-4 h-4" aria-hidden="true" />
    </span>
  );
}

// ─── Form dialog ────────────────────────────────────────────────────────

interface WorkflowFormDialogProps {
  workflow: Workflow | null;
  defaultDeptSlug: string | null;
  onClose: () => void;
  onSave: (input: CreateWorkflowInput | UpdateWorkflowInput) => Promise<void> | void;
  pending: string | null;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

function WorkflowFormDialog({
  workflow,
  defaultDeptSlug,
  onClose,
  onSave,
  pending,
}: WorkflowFormDialogProps) {
  const departments = useDepartments();
  const users = useUsers();
  const isEdit = !!workflow;

  const [deptSlug] = useState(workflow?.deptSlug ?? defaultDeptSlug ?? '');
  const [nome, setNome] = useState(workflow?.nome ?? '');
  const [slug, setSlug] = useState(workflow?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(false);
  const [descricao, setDescricao] = useState(workflow?.descricao ?? '');
  const [cor, setCor] = useState(workflow?.cor ?? '');
  const [icone, setIcone] = useState(workflow?.icone ?? '');
  const [ordem, setOrdem] = useState(workflow?.ordem ?? 0);
  const [defaultFlag, setDefaultFlag] = useState(workflow?.defaultFlag ?? false);
  const [ativo, setAtivo] = useState(workflow?.ativo ?? true);
  const [responsavelId, setResponsavelId] = useState<string>(workflow?.responsavelId ?? '');

  function handleNomeChange(v: string) {
    setNome(v);
    if (!slugTouched && !isEdit) setSlug(slugify(v));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isEdit) {
      const update: UpdateWorkflowInput = {
        slug,
        nome,
        descricao: descricao || null,
        cor: cor || null,
        icone: icone || null,
        ordem,
        defaultFlag,
        ativo,
        responsavelId: responsavelId || null,
      };
      void onSave(update);
    } else {
      const create: CreateWorkflowInput = {
        deptSlug,
        slug,
        nome,
        descricao: descricao || null,
        cor: cor || null,
        icone: icone || null,
        ordem,
        defaultFlag,
        ativo,
        responsavelId: responsavelId || null,
      };
      void onSave(create);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="md">
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Editar fluxo de trabalho' : 'Novo fluxo de trabalho'}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <div className="space-y-3">
            <div>
              <Label htmlFor="wf-dept">Departamento</Label>
              <Select id="wf-dept" value={deptSlug} disabled>
                {departments.map((d) => (
                  <option key={d.slug} value={d.slug}>
                    {d.nome}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                O departamento não muda depois que o fluxo é criado.
              </p>
            </div>
            <div>
              <Label htmlFor="wf-nome" required>
                Nome
              </Label>
              <Input
                id="wf-nome"
                value={nome}
                onChange={(e) => handleNomeChange(e.target.value)}
                placeholder="Ex: Vendas Novas"
                data-testid="wf-form-nome"
                required
              />
            </div>
            <div>
              <Label htmlFor="wf-slug" required>
                <span className="inline-flex items-center gap-1">
                  Identificador
                  <HelpTip texto="Nome curto que o sistema usa por baixo dos panos. Você precisa dele quando aponta uma automação pra este fluxo. É preenchido sozinho a partir do nome." />
                </span>
              </Label>
              <Input
                id="wf-slug"
                value={slug}
                onChange={(e) => {
                  setSlug(slugify(e.target.value));
                  setSlugTouched(true);
                }}
                placeholder="vendas-novas"
                pattern="^[a-z][a-z0-9-]*$"
                data-testid="wf-form-slug"
                required
              />
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                Preenchido a partir do nome. Só letras minúsculas, números e hífen.
              </p>
            </div>
            <div>
              <Label htmlFor="wf-desc">Descrição (opcional)</Label>
              <Input
                id="wf-desc"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Pra que serve este fluxo?"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="wf-cor">Cor (opcional)</Label>
                <Input
                  id="wf-cor"
                  type="color"
                  value={cor || '#999999'}
                  onChange={(e) => setCor(e.target.value)}
                />
              </div>
              <div>
                <Label>Ícone (opcional)</Label>
                {/* BUG-V2-006 fix: substitui <Input> text por <IconPicker>
                    com lista curada de ~70 lucide-react (R5.2 Bloco I).
                    Antes: user digitava nome livre e bundle nunca renderizava. */}
                <IconPicker value={icone || null} onChange={(v) => setIcone(v ?? '')} />
              </div>
            </div>
            <div>
              <Label htmlFor="wf-ordem">Posição no menu lateral</Label>
              <Input
                id="wf-ordem"
                type="number"
                min={0}
                max={9999}
                value={ordem}
                onChange={(e) => setOrdem(Number(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="wf-responsavel">Responsável (opcional)</Label>
              <Select
                id="wf-responsavel"
                value={responsavelId}
                onChange={(e) => setResponsavelId(e.target.value)}
                data-testid="wf-form-responsavel"
              >
                <option value="">— Sem responsável —</option>
                {users
                  .filter((u) => u.ativo)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))}
              </Select>
              {/* FASE-C (copy): antes dizia "estratégia workflow_owner em
                  automações cross-workflow" — jargão de dev na cara do admin. */}
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                Aparece como &quot;Gerenciado por&quot; no topo do quadro. É também quem
                recebe os cards quando uma automação de outro fluxo escolhe
                &quot;responsável pelo fluxo de destino&quot;.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Switch
                checked={defaultFlag}
                onChange={(e) => setDefaultFlag(e.target.checked)}
                label="Fluxo padrão do departamento"
                data-testid="wf-form-default"
              />
              <Switch
                checked={ativo}
                onChange={(e) => setAtivo(e.target.checked)}
                label="Ativo"
              />
            </div>

            {/* BUG-V2-020 / BF.1: gerenciamento de colunas só em edição
                (workflow precisa existir pra ter slug consultável). */}
            {isEdit && workflow && (
              <WorkflowColumnsSection
                workflowSlug={workflow.slug}
                deptSlug={workflow.deptSlug}
              />
            )}
          </div>
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={pending === (workflow?.id ?? 'create')}>
            {isEdit ? 'Salvar' : 'Criar'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
